// client/src/coms/views/watch_together_parts/useWatchTogetherController.js
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  canLocalControlWatch,
  localWatchPerms,
  getWatchQueueSnapshot,
  publishWatchControlMode,
  publishWatchQueuePlayback,
  publishWatchQueueSkipVote,
  publishWatchQueueState,
  requestWatchQueueState,
  subscribeWatchQueue,
  subscribeWatchQueuePlayback,
  reconcileWatchHost,
} from './sessionQueue';
import { canControl, computeCorrection, describeYouTubeError, detectLocalIntent } from './watchSync';
import { WATCH_PERMS, hasPerm } from './watchPermissions';
import {
  buildEmbedUrlForItem,
  buildSpotifyEmbedUrl,
  buildYouTubeEmbedUrl,
  computePlaybackTime,
  computeRemainingSeconds,
  getActiveVoiceRoom,
  getServerKey,
  itemMediaKey,
  normalisePlayback,
  normalizeDraftInput,
  normalizeSkipVote,
  playbackMediaKey,
  queueEntryFromDraft,
  safeNumber,
  safeProvider,
} from './watchTogetherUtils';
import {
  END_STALL_WINDOW_MS,
  END_THRESHOLD_SECONDS,
  INFO_POLL_MS,
  PROVIDERS,
  YT_PLAYER_STATE_BUFFERING,
  YT_PLAYER_STATE_CUED,
  YT_PLAYER_STATE_ENDED,
  YT_PLAYER_STATE_PAUSED,
  YT_PLAYER_STATE_PLAYING,
  YT_PLAYER_STATE_UNSTARTED,
} from './watchTogetherConstants';
import {
  bindSpotifyDeepLinkAuth,
  clearSpotifySession,
  ensureFreshSpotifySession,
  getSpotifySession,
  startSpotifyLogin,
} from './spotifyAuth';
import {
  publishRemoteSpotifyPlayback,
  subscribeRemoteSpotifyPlayback,
} from './remoteSpotifyPlaybackRouter';

let spotifyIframeApiPromise = null;

function loadSpotifyIframeApi() {
  if (typeof window === 'undefined') return Promise.resolve(null);
  if (window.__PA_SPOTIFY_IFRAME_API__) {
    return Promise.resolve(window.__PA_SPOTIFY_IFRAME_API__);
  }
  if (spotifyIframeApiPromise) return spotifyIframeApiPromise;

  spotifyIframeApiPromise = new Promise((resolve) => {
    const previousReady = window.onSpotifyIframeApiReady;

    window.onSpotifyIframeApiReady = (IFrameAPI) => {
      try {
        if (typeof previousReady === 'function') previousReady(IFrameAPI);
      } catch {}
      try {
        window.__PA_SPOTIFY_IFRAME_API__ = IFrameAPI || null;
      } catch {}
      resolve(IFrameAPI || null);
    };

    const existing = document.querySelector('script[data-pa-spotify-iframe-api="1"]');
    if (existing) return;

    const script = document.createElement('script');
    script.src = 'https://open.spotify.com/embed/iframe-api/v1';
    script.async = true;
    script.dataset.paSpotifyIframeApi = '1';
    script.onerror = () => resolve(null);
    document.body.appendChild(script);
  });

  return spotifyIframeApiPromise;
}

function buildPlaybackFromItem(item, playing = false, currentTime = 0) {
  if (!item) return null;
  return {
    provider: safeProvider(item.provider),
    mediaId: String(item.mediaId || '').trim(),
    mediaType: String(item.mediaType || '').trim(),
    mediaUri: String(item.mediaUri || '').trim(),
    currentTime: safeNumber(currentTime, 0),
    playing: !!playing,
    updatedAt: Date.now(),
  };
}

function wait(ms) {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

export function useWatchTogetherController({ server, user, voiceRoom, voiceSession }) {
  const initialSnapshot = getWatchQueueSnapshot();

  const [provider, setProvider] = useState('youtube');
  const [input, setInput] = useState('');
  const [sharedQueue, setSharedQueue] = useState(() => initialSnapshot.queue || []);
  const [activeQueueId, setActiveQueueId] = useState(() => initialSnapshot.activeQueueId || '');
  const [skipVote, setSkipVote] = useState(() => normalizeSkipVote(initialSnapshot.skipVote));
  const [syncStatus, setSyncStatus] = useState('Waiting for queue');
  const [controlMode, setControlModeState] = useState(() => initialSnapshot.controlMode || 'everyone');
  const [hostIdentity, setHostIdentity] = useState(() => initialSnapshot.host || '');
  // Browsers block unmuted autoplay in tabs the user has not interacted with (late joiners).
  const [autoplayMuted, setAutoplayMuted] = useState(false);
  const playAttemptsRef = useRef(0);
  const [playerError, setPlayerError] = useState('');
  const [localProvider, setLocalProvider] = useState('');
  const [localMediaKey, setLocalMediaKey] = useState('');
  const [localEmbedUrl, setLocalEmbedUrl] = useState('');
  const [playerLoaded, setPlayerLoaded] = useState(false);
  const [sharedPlayback, setSharedPlayback] = useState(() => normalisePlayback(initialSnapshot.playback));
  const [spotifySession, setSpotifySession] = useState(() => getSpotifySession());
  const [spotifyAuthBusy, setSpotifyAuthBusy] = useState(false);
  const [spotifyPlayerActivated, setSpotifyPlayerActivated] = useState(false);
  const [spotifyPlayerActivationBusy, setSpotifyPlayerActivationBusy] = useState(false);
  const [spotifyEmbedPrimed, setSpotifyEmbedPrimed] = useState(false);
  const [playerInfo, setPlayerInfo] = useState({
    currentTime: 0,
    duration: 0,
    playerState: YT_PLAYER_STATE_UNSTARTED,
    loadedFraction: 0,
    videoDataId: '',
    lastMessageAt: 0,
  });

  const iframeRef = useRef(null);
  const mediaRef = useRef(null);
  const spotifyMountRef = useRef(null);
  const spotifyPlayerRef = useRef(null);
  const spotifyControllerUriRef = useRef('');
  const spotifyControllerReadyRef = useRef(false);
  const spotifyControllerCreatePromiseRef = useRef(null);
  const spotifyHostRef = useRef(null);
  const lastSyncedActiveMediaKeyRef = useRef('');
  const pendingPlaybackRef = useRef(null);
  const infoRequestTimerRef = useRef(null);
  const bootstrapTimerRef = useRef(null);
  // Changes the player reports before this time were caused by our own commands, not the viewer.
  const ignoreLocalIntentUntilRef = useRef(0);
  const lastPlayerSampleRef = useRef(null);
  const stalledSinceSampleRef = useRef(false);
  const localIntentHandlerRef = useRef(null);

  const sharedQueueRef = useRef(sharedQueue);
  const activeQueueIdRef = useRef(activeQueueId);
  const activeItemRef = useRef(null);
  const sharedPlaybackRef = useRef(sharedPlayback);
  const skipVoteRef = useRef(skipVote);
  const playerInfoRef = useRef(playerInfo);
  const lastKnownPlayerStateRef = useRef(YT_PLAYER_STATE_UNSTARTED);
  // Latest merged infoDelivery fields for the current embed (YouTube only sends fields that changed).
  const ytInfoRef = useRef(null);
  const lastLocalSpotifyRemoteCommandRef = useRef({
    mediaKey: '',
    action: '',
    at: 0,
    updatedAt: 0,
  });
  const lastLocalSpotifySharedPublishRef = useRef({
    mediaKey: '',
    playing: false,
    updatedAt: 0,
    at: 0,
  });
  const lastAppliedRemoteSpotifyCommandRef = useRef({
    mediaKey: '',
    action: '',
    updatedAt: 0,
  });
  const lastAppliedSpotifyBootstrapRef = useRef({
    mediaKey: '',
    playing: false,
    updatedAt: 0,
    at: 0,
  });
  const spotifyBootstrapGuardRef = useRef({
    mediaKey: '',
    until: 0,
  });
  const handledEndedRef = useRef({
    queueId: '',
    provider: '',
    at: 0,
  });
  const advanceQueueFromActiveRef = useRef(null);
  const pendingSpotifyAutoStartRef = useRef(null);
  const spotifyPeerPositionsRef = useRef({});
  const lastPublishedSpotifyProgressRef = useRef({
    mediaKey: '',
    timeBucket: -1,
    at: 0,
  });
  const lastObservedSpotifyPauseRef = useRef(true);
  const spotifySuppressLocalResumeUntilRef = useRef(0);

  const advanceInFlightRef = useRef(false);
  const lastAdvancedQueueIdRef = useRef('');
  const progressRef = useRef({
    queueId: '',
    time: -1,
    seenAt: 0,
  });

  useEffect(() => {
    sharedQueueRef.current = sharedQueue;
  }, [sharedQueue]);

  useEffect(() => {
    activeQueueIdRef.current = activeQueueId;
  }, [activeQueueId]);

  useEffect(() => {
    sharedPlaybackRef.current = sharedPlayback;
  }, [sharedPlayback]);

  useEffect(() => {
    skipVoteRef.current = skipVote;
  }, [skipVote]);

  useEffect(() => {
    playerInfoRef.current = playerInfo;
  }, [playerInfo]);

  // A new embed starts from scratch: no carried-over player fields or seek baseline.
  useEffect(() => {
    ytInfoRef.current = null;
    lastPlayerSampleRef.current = null;
    stalledSinceSampleRef.current = false;
  }, [localEmbedUrl]);

  useEffect(() => {
    activeItemRef.current = null;
    if (!sharedQueue.length) return;
    if (activeQueueId) {
      const found = sharedQueue.find((item) => item.id === activeQueueId);
      if (found) {
        activeItemRef.current = found;
        return;
      }
    }
    activeItemRef.current = sharedQueue[0] || null;
  }, [sharedQueue, activeQueueId]);

  useEffect(() => {
    let alive = true;
    let detach = null;

    (async () => {
      try {
        const fresh = await ensureFreshSpotifySession();
        if (alive) setSpotifySession(fresh);
      } catch {}

      try {
        detach = await bindSpotifyDeepLinkAuth(
          (session) => {
            if (!alive) return;
            setSpotifyAuthBusy(false);
            setSpotifySession(session);
            setPlayerError('');
            setSyncStatus('Spotify connected');
          },
          (error) => {
            if (!alive) return;
            setSpotifyAuthBusy(false);
            setPlayerError(error?.message || 'Spotify login failed.');
          }
        );
      } catch {}
    })();

    return () => {
      alive = false;
      try {
        if (typeof detach === 'function') detach();
      } catch {}
    };
  }, []);

  const normalizedDraft = useMemo(() => normalizeDraftInput(input, provider), [input, provider]);
  const serverId = useMemo(() => getServerKey(server), [server]);
  const room = getActiveVoiceRoom(voiceRoom);
  const identity = String(room?.localParticipant?.identity || '').trim();
  const userName = String((user && (user.display_name || user.username || user.name)) || '').trim() || 'Someone';
  const shareReady = !!(room && room.localParticipant && serverId);

  const participantCount = useMemo(() => {
    const localCount = room && room.localParticipant ? 1 : 0;
    const remoteCount = room && room.remoteParticipants ? room.remoteParticipants.size : 0;
    return localCount + remoteCount;
  }, [room, room?.localParticipant, room?.remoteParticipants, room?.remoteParticipants?.size]);

  const majorityNeeded = useMemo(() => {
    if (!participantCount) return 1;
    return Math.floor(participantCount / 2) + 1;
  }, [participantCount]);

  const activeItem = useMemo(() => {
    if (!sharedQueue.length) return null;
    if (activeQueueId) {
      const found = sharedQueue.find((item) => item.id === activeQueueId);
      if (found) return found;
    }
    return sharedQueue[0] || null;
  }, [activeQueueId, sharedQueue]);

  const activeSkipVotes = useMemo(() => {
    if (!activeItem) return [];
    if (!skipVote || skipVote.queueId !== activeItem.id) return [];
    return Array.isArray(skipVote.identities) ? skipVote.identities : [];
  }, [activeItem, skipVote]);

  const hasVotedToSkip = !!(identity && activeSkipVotes.includes(identity));
  const activeMediaKey = itemMediaKey(activeItem);
  const isShowingActiveLocally = !!(activeItem && localMediaKey && activeMediaKey && localMediaKey === activeMediaKey);

  const registerSpotifyPeerProgress = useCallback((identityKey, mediaKey, currentTime) => {
    const who = String(identityKey || '').trim();
    const key = String(mediaKey || '').trim();
    if (!who || !key) return;

    const nextTime = Math.max(0, safeNumber(currentTime, 0));
    spotifyPeerPositionsRef.current[who] = {
      mediaKey: key,
      currentTime: nextTime,
      updatedAt: Date.now(),
    };
  }, []);

  const getFurthestSpotifyProgressTime = useCallback((mediaKey, fallbackTime = 0) => {
    const key = String(mediaKey || '').trim();
    const now = Date.now();
    let furthest = Math.max(0, safeNumber(fallbackTime, 0));

    Object.values(spotifyPeerPositionsRef.current).forEach((entry) => {
      if (!entry || entry.mediaKey !== key) return;
      if (now - safeNumber(entry.updatedAt, 0) > 8000) return;
      furthest = Math.max(furthest, Math.max(0, safeNumber(entry.currentTime, 0)));
    });

    return furthest;
  }, []);

  const publishSpotifyPeerProgress = useCallback(async (currentTime) => {
    const currentActive = activeItemRef.current;
    const currentMediaKey = itemMediaKey(currentActive);
    if (!currentActive || safeProvider(currentActive.provider) !== 'spotify') return false;
    if (!currentMediaKey || !shareReady) return false;

    const bucket = Math.floor(Math.max(0, safeNumber(currentTime, 0)));
    const last = lastPublishedSpotifyProgressRef.current;
    if (
      last.mediaKey === currentMediaKey &&
      last.timeBucket === bucket &&
      Date.now() - safeNumber(last.at, 0) < 900
    ) {
      return false;
    }

    lastPublishedSpotifyProgressRef.current = {
      mediaKey: currentMediaKey,
      timeBucket: bucket,
      at: Date.now(),
    };

    registerSpotifyPeerProgress(identity, currentMediaKey, currentTime);

    return await publishRemoteSpotifyPlayback({
      serverId,
      mediaId: currentActive.mediaId,
      mediaType: currentActive.mediaType,
      mediaUri: currentActive.mediaUri,
      currentTime,
      action: 'progress',
    }, room);
  }, [identity, registerSpotifyPeerProgress, room, serverId, shareReady]);

  useEffect(() => {
    if (!activeItem || safeProvider(activeItem.provider) !== 'spotify') {
      setSpotifyEmbedPrimed(false);
      return;
    }
  }, [activeItem]);

  const ensureSpotifyVisibleHost = useCallback(() => {
    if (typeof document === 'undefined') return null;

    const mountEl = spotifyMountRef.current;
    if (!mountEl) return null;

    let hostEl = spotifyHostRef.current;
    if (!hostEl) {
      hostEl = document.createElement('div');
      hostEl.dataset.paSpotifyHost = '1';
      hostEl.style.width = '100%';
      hostEl.style.height = '100%';
      hostEl.style.display = 'block';
      spotifyHostRef.current = hostEl;
    }

    try {
      if (hostEl.parentNode !== mountEl) {
        mountEl.appendChild(hostEl);
      }
      hostEl.style.width = '100%';
      hostEl.style.height = '100%';
      hostEl.style.display = 'block';
    } catch {}

    return hostEl;
  }, []);

  const sendPlayerCommand = useCallback((func, args = []) => {
    try {
      const frame = iframeRef.current;
      const target = frame && frame.contentWindow ? frame.contentWindow : null;
      if (!target) return false;
      // Our own seek: start a new baseline so its jump is never compared against a pre-seek sample.
      if (func === 'seekTo' || func === 'loadVideoById' || func === 'cueVideoById') {
        lastPlayerSampleRef.current = null;
        stalledSinceSampleRef.current = false;
      }
      target.postMessage(
        JSON.stringify({
          event: 'command',
          func,
          args,
        }),
        '*'
      );
      return true;
    } catch {
      return false;
    }
  }, []);

  const bootstrapPlayerMessaging = useCallback(() => {
    try {
      const frame = iframeRef.current;
      const target = frame && frame.contentWindow ? frame.contentWindow : null;
      if (!target) return false;

      target.postMessage(
        JSON.stringify({
          event: 'listening',
          id: `watch-together-${localMediaKey || 'player'}`,
          channel: 'widget',
        }),
        '*'
      );

      target.postMessage('{"event":"listening","channel":"widget"}', '*');
      // Ask the player to report errors (unavailable / not embeddable videos).
      target.postMessage(JSON.stringify({ event: 'command', func: 'addEventListener', args: ['onError'], channel: 'widget' }), '*');
      return true;
    } catch {
      return false;
    }
  }, [localMediaKey]);

  const pushQueue = useCallback(async (queue, nextActiveQueueId, playback = undefined, nextSkipVote = undefined) => {
    const ok = await publishWatchQueueState(
      {
        serverId,
        queue,
        activeQueueId: nextActiveQueueId,
        playback,
        skipVote: nextSkipVote,
      },
      room
    );
    setSyncStatus(ok ? 'Shared queue live' : 'Queue send failed');
    return ok;
  }, [room, serverId]);

  const publishSpotifySharedPlayback = useCallback(async (playback) => {
    const p = normalisePlayback(playback);
    if (!p || safeProvider(p.provider) !== 'spotify') return;

    lastLocalSpotifySharedPublishRef.current = {
      mediaKey: playbackMediaKey(p),
      playing: !!p.playing,
      updatedAt: safeNumber(p.updatedAt, Date.now()),
      at: Date.now(),
    };

    await publishWatchQueuePlayback({ serverId, ...p }, room);
  }, [room, serverId]);

  const destroySpotifyController = useCallback(() => {
    const controller = spotifyPlayerRef.current;
    if (controller && typeof controller.destroy === 'function') {
      try {
        controller.destroy();
      } catch {}
    }

    spotifyPlayerRef.current = null;
    spotifyControllerUriRef.current = '';
    spotifyControllerReadyRef.current = false;
    spotifyControllerCreatePromiseRef.current = null;
    setSpotifyPlayerActivated(false);

    const hostEl = spotifyHostRef.current;
    if (hostEl) {
      try {
        hostEl.innerHTML = '';
      } catch {}
    }
  }, []);

  const ensureSpotifyEmbedController = useCallback(async () => {
    const currentActive = activeItemRef.current;
    if (!currentActive || safeProvider(currentActive.provider) !== 'spotify') return null;

    const uri = String(currentActive.mediaUri || '').trim();
    if (!uri) throw new Error('Spotify item is missing a valid URI.');

    const hostEl = ensureSpotifyVisibleHost();
    if (!hostEl) return null;

    if (spotifyPlayerRef.current && spotifyControllerReadyRef.current) {
      return spotifyPlayerRef.current;
    }

    if (spotifyControllerCreatePromiseRef.current) {
      return spotifyControllerCreatePromiseRef.current;
    }

    const createPromise = (async () => {
      const IFrameAPI = await loadSpotifyIframeApi();
      if (!IFrameAPI || typeof IFrameAPI.createController !== 'function') {
        throw new Error('Spotify embed API could not be loaded.');
      }

      const oldController = spotifyPlayerRef.current;
      if (oldController && typeof oldController.destroy === 'function') {
        try {
          oldController.destroy();
        } catch {}
      }
      spotifyPlayerRef.current = null;
      spotifyControllerUriRef.current = '';
      spotifyControllerReadyRef.current = false;

      return await new Promise((resolve, reject) => {
        let settled = false;
        let readyTimeout = null;

        const finishResolve = (controller) => {
          if (settled) return;
          settled = true;
          if (readyTimeout) clearTimeout(readyTimeout);
          spotifyControllerReadyRef.current = true;
          spotifyControllerCreatePromiseRef.current = null;
          setSpotifyPlayerActivated(true);
          setSpotifyEmbedPrimed(true);
          setPlayerLoaded(true);
          setPlayerError('');
          resolve(controller);
        };

        const finishReject = (error) => {
          if (settled) return;
          settled = true;
          if (readyTimeout) clearTimeout(readyTimeout);
          spotifyControllerReadyRef.current = false;
          spotifyControllerCreatePromiseRef.current = null;
          reject(error);
        };

        try {
          hostEl.innerHTML = '';
        } catch {}

        try {
          IFrameAPI.createController(
            hostEl,
            {
              uri,
              width: '100%',
              height: '100%',
              theme: 'dark',
            },
            (controller) => {
              if (!controller) {
                finishReject(new Error('Spotify embed controller could not be created.'));
                return;
              }

              spotifyPlayerRef.current = controller;
              spotifyControllerUriRef.current = uri;
              spotifyControllerReadyRef.current = false;
              setPlayerError('');

              try {
                controller.addListener('ready', () => {
                  console.log('[WatchTogether][Spotify] Spotify controller ready');
                  finishResolve(controller);
                });
              } catch {}

              try {
                controller.addListener('playback_started', (event) => {
                  console.log('[WatchTogether][Spotify] Playback started event received');
                  const playingUri = String(event?.data?.playingURI || spotifyControllerUriRef.current || uri).trim();
                  spotifyControllerUriRef.current = playingUri || uri;
                  setPlayerLoaded(true);
                  setPlayerError('');
                  setSpotifyPlayerActivated(true);
                  setSpotifyEmbedPrimed(true);
                });
              } catch {}

              try {
                controller.addListener('playback_update', (event) => {
                  const data = event && event.data ? event.data : {};
                  const duration = safeNumber(data.duration, 0) / 1000;
                  const currentTime = safeNumber(data.position, 0) / 1000;
                  const paused = !!data.isPaused;
                  const playingUri = String(data.playingURI || spotifyControllerUriRef.current || uri).trim();

                  if (playingUri) {
                    spotifyControllerUriRef.current = playingUri;
                  }

                  setPlayerLoaded(true);
                  setPlayerError('');
                  setSpotifyPlayerActivated(true);
                  setSpotifyEmbedPrimed(true);
                  setPlayerInfo({
                    currentTime,
                    duration,
                    playerState: paused ? YT_PLAYER_STATE_UNSTARTED : YT_PLAYER_STATE_PLAYING,
                    loadedFraction: duration > 0 ? Math.min(1, currentTime / duration) : 0,
                    videoDataId: playingUri,
                    lastMessageAt: Date.now(),
                  });

                  const currentActiveItem = activeItemRef.current;
                  const queueId = String((currentActiveItem && currentActiveItem.id) || '').trim();
                  const currentMediaKey = itemMediaKey(currentActiveItem);
                  const guard = spotifyBootstrapGuardRef.current;
                  const remaining = computeRemainingSeconds(currentTime, duration);
                  const wasPaused = !!lastObservedSpotifyPauseRef.current;

                  if (currentMediaKey) {
                    registerSpotifyPeerProgress(identity, currentMediaKey, currentTime);
                    if (!paused) {
                      publishSpotifyPeerProgress(currentTime).catch(() => {});
                    }
                  }

                  if (
                    currentMediaKey &&
                    wasPaused &&
                    !paused &&
                    Date.now() >= safeNumber(spotifySuppressLocalResumeUntilRef.current, 0)
                  ) {
                    const furthestTime = getFurthestSpotifyProgressTime(currentMediaKey, currentTime);
                    if (furthestTime > currentTime + 0.75) {
                      spotifySuppressLocalResumeUntilRef.current = Date.now() + 3000;
                      directPlaySpotifyLocally(furthestTime).catch(() => {});
                    }
                  }

                  lastObservedSpotifyPauseRef.current = paused;

                  if (
                    queueId &&
                    currentMediaKey &&
                    guard.mediaKey === currentMediaKey &&
                    safeNumber(guard.until, 0) > Date.now() &&
                    currentTime >= 3
                  ) {
                    spotifyBootstrapGuardRef.current = {
                      mediaKey: '',
                      until: 0,
                    };
                  }

                  if (
                    queueId &&
                    currentMediaKey &&
                    !paused &&
                    duration > 0 &&
                    remaining <= END_THRESHOLD_SECONDS &&
                    !(guard.mediaKey === currentMediaKey && safeNumber(guard.until, 0) > Date.now()) &&
                    !advanceInFlightRef.current &&
                    lastAdvancedQueueIdRef.current !== queueId
                  ) {
                    const handled = handledEndedRef.current;
                    if (!(handled.queueId === queueId && handled.provider === 'spotify' && Date.now() - safeNumber(handled.at, 0) < 4000)) {
                      handledEndedRef.current = {
                        queueId,
                        provider: 'spotify',
                        at: Date.now(),
                      };
                      try {
                        if (typeof advanceQueueFromActiveRef.current === 'function') {
                          advanceQueueFromActiveRef.current(queueId, currentMediaKey);
                        }
                      } catch {}
                    }
                  }
                });
              } catch {}

              readyTimeout = setTimeout(() => {
                console.log('[WatchTogether][Spotify] Spotify controller ready fallback');
                finishResolve(controller);
              }, 2500);
            }
          );
        } catch (error) {
          finishReject(error);
        }
      });
    })();

    spotifyControllerCreatePromiseRef.current = createPromise;
    return createPromise;
  }, [ensureSpotifyVisibleHost, getFurthestSpotifyProgressTime, identity, publishSpotifyPeerProgress, registerSpotifyPeerProgress]);

  const loadSpotifyItemIntoController = useCallback(async (targetUri, startTime = 0, shouldPlay = false) => {
    const currentActive = activeItemRef.current;
    if (!currentActive || safeProvider(currentActive.provider) !== 'spotify') return false;

    const uri = String(targetUri || currentActive.mediaUri || '').trim();
    if (!uri) {
      setPlayerError('Spotify item is missing a valid URI.');
      return false;
    }

    ensureSpotifyVisibleHost();

    const controller = await ensureSpotifyEmbedController();
    if (!controller) {
      setPlayerError('Spotify controller is not ready.');
      return false;
    }

    ensureSpotifyVisibleHost();

    const targetTime = Math.max(0, Math.floor(safeNumber(startTime, 0)));
    const uriChanged = spotifyControllerUriRef.current !== uri;

    if (uriChanged && typeof controller.loadUri === 'function') {
      try {
        controller.loadUri(uri, false, targetTime, 'dark');
        spotifyControllerUriRef.current = uri;
        setPlayerLoaded(false);
        await wait(120);
      } catch {}
    } else if (typeof controller.seek === 'function') {
      try {
        controller.seek(targetTime);
      } catch {}
    }

    if (shouldPlay) {
      if (typeof controller.resume === 'function') {
        controller.resume();
      } else if (typeof controller.play === 'function') {
        controller.play();
      }
    } else if (typeof controller.pause === 'function') {
      try {
        controller.pause();
      } catch {}
    }

    setSpotifyPlayerActivated(true);
    setSpotifyEmbedPrimed(true);
    setPlayerLoaded(true);
    setPlayerError('');
    return true;
  }, [ensureSpotifyEmbedController, ensureSpotifyVisibleHost]);

  const directPlaySpotifyLocally = useCallback(async (startTime = 0) => {
    const currentActive = activeItemRef.current;
    if (!currentActive || safeProvider(currentActive.provider) !== 'spotify') return false;

    const uri = String(currentActive.mediaUri || '').trim();
    if (!uri) {
      setPlayerError('Spotify item is missing a valid URI.');
      return false;
    }

    console.log('[WatchTogether][Spotify] Play button pressed');
    spotifySuppressLocalResumeUntilRef.current = Date.now() + 2500;

    try {
      return await loadSpotifyItemIntoController(uri, startTime, true);
    } catch (error) {
      setPlayerError(error?.message || 'Spotify playback failed.');
      return false;
    }
  }, [loadSpotifyItemIntoController]);

  const directPauseSpotifyLocally = useCallback(async (pauseTime = 0) => {
    const currentActive = activeItemRef.current;
    if (!currentActive || safeProvider(currentActive.provider) !== 'spotify') return false;

    const uri = String(currentActive.mediaUri || '').trim();
    if (!uri) {
      setPlayerError('Spotify item is missing a valid URI.');
      return false;
    }

    spotifySuppressLocalResumeUntilRef.current = Date.now() + 2500;

    try {
      return await loadSpotifyItemIntoController(uri, pauseTime, false);
    } catch (error) {
      setPlayerError(error?.message || 'Spotify pause failed.');
      return false;
    }
  }, [loadSpotifyItemIntoController]);

  const syncSpotifyBeforeLocalPlaybackStarts = useCallback(async (playback, options = {}) => {
    const p = normalisePlayback(playback);
    if (!p || safeProvider(p.provider) !== 'spotify') return false;

    const currentActive = activeItemRef.current;
    const activeKey = itemMediaKey(currentActive);
    const playbackKey = playbackMediaKey(p);
    if (!currentActive || safeProvider(currentActive.provider) !== 'spotify') return false;
    if (!activeKey || !playbackKey || activeKey !== playbackKey) return false;

    const force = !!options.force;
    const info = playerInfoRef.current || {};
    const isLocalPlaying = safeNumber(info.playerState, YT_PLAYER_STATE_UNSTARTED) === YT_PLAYER_STATE_PLAYING;

    if (!force && isLocalPlaying) return false;

    const lastLocal = lastLocalSpotifySharedPublishRef.current;
    if (
      !force &&
      lastLocal.mediaKey === playbackKey &&
      lastLocal.playing === !!p.playing &&
      Date.now() - safeNumber(lastLocal.at, 0) < 2000
    ) {
      return false;
    }

    const lastApplied = lastAppliedSpotifyBootstrapRef.current;
    if (
      !force &&
      lastApplied.mediaKey === playbackKey &&
      lastApplied.playing === !!p.playing &&
      lastApplied.updatedAt === safeNumber(p.updatedAt, 0)
    ) {
      return false;
    }

    lastAppliedSpotifyBootstrapRef.current = {
      mediaKey: playbackKey,
      playing: !!p.playing,
      updatedAt: safeNumber(p.updatedAt, 0),
      at: Date.now(),
    };

    spotifyBootstrapGuardRef.current = {
      mediaKey: playbackKey,
      until: Date.now() + 8000,
    };

    if (p.playing) {
      return await directPlaySpotifyLocally(computePlaybackTime(p));
    }

    return await directPauseSpotifyLocally(safeNumber(p.currentTime, 0));
  }, [directPauseSpotifyLocally, directPlaySpotifyLocally]);

  const markSpotifyEmbedPrimed = useCallback(async () => {
    const currentActive = activeItemRef.current;
    if (!currentActive || safeProvider(currentActive.provider) !== 'spotify') return;
    if (spotifyPlayerActivationBusy) return;

    setSpotifyPlayerActivationBusy(true);
    setPlayerError('');

    try {
      ensureSpotifyVisibleHost();
      await ensureSpotifyEmbedController();
      ensureSpotifyVisibleHost();
      setSpotifyEmbedPrimed(true);
      setSpotifyPlayerActivated(true);
      setPlayerLoaded(true);
    } catch (error) {
      setPlayerError(error?.message || 'Could not enable Spotify on this browser.');
    } finally {
      setSpotifyPlayerActivationBusy(false);
    }
  }, [ensureSpotifyEmbedController, ensureSpotifyVisibleHost, spotifyPlayerActivationBusy]);

  const applyPlaybackToPlayer = useCallback((playback) => {
    const p = normalisePlayback(playback);
    if (!p) return;

    const providerName = safeProvider(p.provider);
    if (providerName === 'spotify') {
      return;
    }

    const active = activeItemRef.current;
    const activeKey = itemMediaKey(active);
    const playbackKey = playbackMediaKey(p);
    if (!active || !activeKey || activeKey !== playbackKey) return;

    const targetTime = computePlaybackTime(p);

    if (providerName === 'misc') {
      const nextUrl = buildEmbedUrlForItem(active, { autoplay: p.playing, startSeconds: targetTime });
      const mediaKind = String(active.mediaKind || '').trim().toLowerCase();

      if (localMediaKey !== playbackKey || !localEmbedUrl || localProvider !== 'misc') {
        setPlayerError('');
        setPlayerLoaded(false);
        setLocalProvider('misc');
        setLocalMediaKey(playbackKey);
        setLocalEmbedUrl(nextUrl);
        pendingPlaybackRef.current = p;
        return;
      }

      if (mediaKind !== 'direct') {
        pendingPlaybackRef.current = null;
        return;
      }

      const mediaEl = mediaRef.current;
      if (!playerLoaded || !mediaEl) {
        pendingPlaybackRef.current = p;
        return;
      }

      try {
        const correction = computeCorrection({
          localTime: Number(mediaEl.currentTime || 0),
          localPlaying: !mediaEl.paused,
          localBuffering: Number(mediaEl.readyState) < 3 && !mediaEl.paused,
          playback: p,
        });
        if (correction.seekTo != null) {
          ignoreLocalIntentUntilRef.current = Date.now() + 1500;
          mediaEl.currentTime = correction.seekTo;
        }
      } catch {}

      if (p.playing) {
        const playPromise = mediaEl.play && mediaEl.play();
        if (playPromise && typeof playPromise.catch === 'function') playPromise.catch(() => {});
      } else if (mediaEl.pause) {
        mediaEl.pause();
      }
      return;
    }

    if (localMediaKey !== playbackKey || !localEmbedUrl || localProvider !== 'youtube') {
      setPlayerError('');
      setPlayerLoaded(false);
      setLocalProvider('youtube');
      setLocalMediaKey(playbackKey);
      setLocalEmbedUrl(buildYouTubeEmbedUrl(p.mediaId, p.playing, targetTime));
      return;
    }

    if (!playerLoaded) {
      pendingPlaybackRef.current = p;
      return;
    }

    // Only correct meaningful drift; re-seeking every participant on every update caused stutter.
    const info = playerInfoRef.current || {};
    const state = safeNumber(info.playerState, YT_PLAYER_STATE_UNSTARTED);
    // A paused / ended / cued player sends no updates, so its last report stays valid; a playing one
    // reports several times a second.
    const settledState = state === YT_PLAYER_STATE_PAUSED || state === YT_PLAYER_STATE_ENDED || state === YT_PLAYER_STATE_CUED;
    const fresh = state !== YT_PLAYER_STATE_UNSTARTED && (settledState || Date.now() - safeNumber(info.lastMessageAt, 0) < 2000);
    const correction = fresh
      ? computeCorrection({
          localTime: safeNumber(info.currentTime, 0),
          localPlaying: state === YT_PLAYER_STATE_PLAYING,
          localBuffering: state === YT_PLAYER_STATE_BUFFERING,
          playback: p,
        })
      : { seekTo: targetTime, play: !!p.playing, pause: !p.playing };

    if (correction.seekTo != null || correction.play || correction.pause) {
      ignoreLocalIntentUntilRef.current = Date.now() + 1500;
    }
    if (correction.seekTo != null) sendPlayerCommand('seekTo', [correction.seekTo, true]);
    if (correction.play) {
      playAttemptsRef.current += 1;
      // Two attempts without the player starting: autoplay is blocked. Muted playback is allowed,
      // so join in sync muted and offer a one-click unmute.
      if (playAttemptsRef.current >= 3) {
        sendPlayerCommand('mute');
        setAutoplayMuted(true);
      }
      setTimeout(() => {
        sendPlayerCommand('playVideo');
      }, 120);
    } else if (correction.pause) {
      setTimeout(() => {
        sendPlayerCommand('pauseVideo');
      }, 120);
    }
  }, [localEmbedUrl, localMediaKey, localProvider, playerLoaded, sendPlayerCommand]);

  const resyncToSharedPlayback = useCallback(() => {
    const currentActive = activeItemRef.current;
    const currentPlayback = sharedPlaybackRef.current;
    const activeKey = itemMediaKey(currentActive);
    const playbackKey = playbackMediaKey(currentPlayback);

    if (!currentActive || !activeKey) return;
    if (!currentPlayback || !playbackKey) return;
    if (activeKey !== playbackKey) return;

    pendingPlaybackRef.current = currentPlayback;
    applyPlaybackToPlayer(currentPlayback);
  }, [applyPlaybackToPlayer]);

  const activateSpotifyPlayer = useCallback(async () => {
    await markSpotifyEmbedPrimed();
  }, [markSpotifyEmbedPrimed]);

  const advanceQueueFromActive = useCallback(async (queueId, mediaKey) => {
    const targetQueueId = String(queueId || '').trim();
    const targetMediaKey = String(mediaKey || '').trim();

    if (!targetQueueId || !targetMediaKey) return;
    if (!shareReady) return;
    if (!canLocalControlWatch(room)) return;
    if (advanceInFlightRef.current) return;
    if (lastAdvancedQueueIdRef.current === targetQueueId) return;

    const currentQueue = Array.isArray(sharedQueueRef.current) ? sharedQueueRef.current : [];
    if (!currentQueue.length) return;

    const currentActiveId = String(activeQueueIdRef.current || '').trim();
    const currentActiveItem = currentActiveId
      ? currentQueue.find((item) => item.id === currentActiveId) || null
      : currentQueue[0] || null;

    if (!currentActiveItem) return;
    if (String(currentActiveItem.id || '').trim() !== targetQueueId) return;
    if (itemMediaKey(currentActiveItem) !== targetMediaKey) return;

    advanceInFlightRef.current = true;
    lastAdvancedQueueIdRef.current = targetQueueId;

    try {
      const nextQueue = currentQueue.filter((item) => item.id !== targetQueueId);
      const nextItem = nextQueue[0] || null;
      const nextActiveId = nextItem ? nextItem.id : '';

      if (nextItem) {
        const nextPlayback = buildPlaybackFromItem(nextItem, true, 0);
        const clearedSkipVote = {
          queueId: '',
          identities: [],
          updatedAt: Date.now(),
        };

        setSharedQueue(nextQueue);
        setActiveQueueId(nextActiveId);
        setSharedPlayback(nextPlayback);
        setSkipVote(clearedSkipVote);
        sharedPlaybackRef.current = nextPlayback;
        skipVoteRef.current = clearedSkipVote;
        pendingPlaybackRef.current = safeProvider(nextItem.provider) !== 'spotify' ? nextPlayback : null;

        setPlayerError('');
        setPlayerLoaded(false);
        setLocalProvider(safeProvider(nextItem.provider));
        setLocalMediaKey(itemMediaKey(nextItem));
        setLocalEmbedUrl(buildEmbedUrlForItem(nextItem, true, 0));

        if (safeProvider(nextItem.provider) !== 'spotify') {
          setSpotifyPlayerActivated(false);
        }

        progressRef.current = {
          queueId: nextItem.id,
          time: -1,
          seenAt: 0,
        };

        handledEndedRef.current = {
          queueId: nextItem.id,
          provider: '',
          at: 0,
        };

        await pushQueue(nextQueue, nextActiveId, nextPlayback, clearedSkipVote);

        if (safeProvider(nextItem.provider) === 'spotify') {
          pendingSpotifyAutoStartRef.current = {
            mediaKey: itemMediaKey(nextItem),
            currentTime: 0,
            publishRemote: true,
            publishShared: true,
          };
        } else {
          await publishWatchQueuePlayback({ serverId, ...nextPlayback }, room);
        }
      } else {
        const stoppedPlayback = buildPlaybackFromItem(currentActiveItem, false, 0);
        const clearedSkipVote = {
          queueId: '',
          identities: [],
          updatedAt: Date.now(),
        };

        setSharedQueue([]);
        setActiveQueueId('');
        setSharedPlayback(stoppedPlayback);
        setSkipVote(clearedSkipVote);
        sharedPlaybackRef.current = stoppedPlayback;
        skipVoteRef.current = clearedSkipVote;
        pendingPlaybackRef.current = null;
        pendingSpotifyAutoStartRef.current = null;

        setLocalProvider('');
        setLocalMediaKey('');
        setLocalEmbedUrl('');
        setPlayerLoaded(false);
        setPlayerError('');
        setSpotifyPlayerActivated(false);

        progressRef.current = {
          queueId: '',
          time: -1,
          seenAt: 0,
        };

        handledEndedRef.current = {
          queueId: '',
          provider: '',
          at: 0,
        };

        await pushQueue([], '', stoppedPlayback, clearedSkipVote);

        if (safeProvider(currentActiveItem.provider) !== 'spotify') {
          await publishWatchQueuePlayback({ serverId, ...stoppedPlayback }, room);
        } else {
          await publishSpotifySharedPlayback(stoppedPlayback);
          await publishRemoteSpotifyPlayback({
            serverId,
            mediaId: currentActiveItem.mediaId,
            mediaType: currentActiveItem.mediaType,
            mediaUri: currentActiveItem.mediaUri,
            currentTime: 0,
            action: 'pause',
          }, room);
        }
      }
    } finally {
      setTimeout(() => {
        advanceInFlightRef.current = false;
      }, 1000);
    }
  }, [publishSpotifySharedPlayback, pushQueue, room, serverId, shareReady]);

  useEffect(() => {
    advanceQueueFromActiveRef.current = (queueId, mediaKey) => {
      advanceQueueFromActive(queueId, mediaKey).catch(() => {});
    };
  }, [advanceQueueFromActive]);

  useEffect(() => subscribeWatchQueue((snapshot) => {
    if (snapshot.serverId && serverId && snapshot.serverId !== serverId) return;
    setSharedQueue(snapshot.queue || []);
    setActiveQueueId(snapshot.activeQueueId || '');
    setSharedPlayback(normalisePlayback(snapshot.playback));
    setSkipVote(normalizeSkipVote(snapshot.skipVote));
    setControlModeState(snapshot.controlMode || 'everyone');
    setHostIdentity(snapshot.host || '');
    setSyncStatus(snapshot.queue && snapshot.queue.length ? 'Shared queue live' : 'Waiting for queue');
  }), [serverId]);

  useEffect(() => subscribeWatchQueuePlayback((playback) => {
    const p = normalisePlayback(playback);
    if (!p) return;
    if (serverId && playback && playback.serverId && playback.serverId !== serverId) return;

    setSharedPlayback(p);
    sharedPlaybackRef.current = p;

    if (safeProvider(p.provider) === 'spotify') {
      syncSpotifyBeforeLocalPlaybackStarts(p).catch(() => {});
      return;
    }

    pendingPlaybackRef.current = p;
    applyPlaybackToPlayer(p);
  }), [applyPlaybackToPlayer, serverId, syncSpotifyBeforeLocalPlaybackStarts]);

  useEffect(() => subscribeRemoteSpotifyPlayback((event) => {
    if (!event || safeProvider(event.provider) !== 'spotify') return;
    if (serverId && event.serverId && event.serverId !== serverId) return;

    const currentActive = activeItemRef.current;
    const activeKey = itemMediaKey(currentActive);
    const remoteKey = itemMediaKey({
      provider: 'spotify',
      mediaId: event.mediaId,
      mediaType: event.mediaType,
    });

    if (!currentActive || safeProvider(currentActive.provider) !== 'spotify') return;
    if (!activeKey || !remoteKey || activeKey !== remoteKey) return;

    const action = String(event.action || '').trim().toLowerCase();

    if (action === 'progress') {
      if (event.fromIdentity && event.fromIdentity !== identity) {
        registerSpotifyPeerProgress(event.fromIdentity, remoteKey, safeNumber(event.currentTime, 0));
      }
      return;
    }

    if (action !== 'play' && action !== 'pause') return;

    if (event.fromIdentity && event.fromIdentity !== identity) {
      registerSpotifyPeerProgress(event.fromIdentity, remoteKey, safeNumber(event.currentTime, 0));
    }

    const updatedAt = safeNumber(event.updatedAt, 0);
    const lastApplied = lastAppliedRemoteSpotifyCommandRef.current;
    if (lastApplied.mediaKey === remoteKey && lastApplied.action === action && lastApplied.updatedAt === updatedAt) {
      return;
    }

    const lastLocal = lastLocalSpotifyRemoteCommandRef.current;
    if (lastLocal.mediaKey === remoteKey && lastLocal.action === action && Date.now() - safeNumber(lastLocal.at, 0) < 1500) {
      lastAppliedRemoteSpotifyCommandRef.current = {
        mediaKey: remoteKey,
        action,
        updatedAt,
      };
      return;
    }

    lastAppliedRemoteSpotifyCommandRef.current = {
      mediaKey: remoteKey,
      action,
      updatedAt,
    };

    if (action === 'play') {
      directPlaySpotifyLocally(safeNumber(event.currentTime, 0)).catch(() => {});
    } else {
      directPauseSpotifyLocally(safeNumber(event.currentTime, 0)).catch(() => {});
    }
  }), [directPauseSpotifyLocally, directPlaySpotifyLocally, identity, registerSpotifyPeerProgress, serverId]);

  useEffect(() => {
    if (!shareReady) return;
    requestWatchQueueState(serverId, room).catch(() => {});
  }, [room, serverId, shareReady]);

  useEffect(() => {
    const nextActive = activeItemRef.current;
    const nextActiveKey = itemMediaKey(nextActive);
    const nextActiveQueueId = String((nextActive && nextActive.id) || '').trim();

    if (!nextActive || !nextActiveKey) {
      lastSyncedActiveMediaKeyRef.current = '';
      setLocalProvider('');
      setLocalMediaKey('');
      setLocalEmbedUrl('');
      setPlayerLoaded(false);
      setPlayerError('');
      setSpotifyPlayerActivated(false);
      setSpotifyEmbedPrimed(false);
      pendingPlaybackRef.current = null;
      pendingSpotifyAutoStartRef.current = null;
      spotifyPeerPositionsRef.current = {};
      lastPublishedSpotifyProgressRef.current = {
        mediaKey: '',
        timeBucket: -1,
        at: 0,
      };
      lastObservedSpotifyPauseRef.current = true;
      spotifySuppressLocalResumeUntilRef.current = 0;
      lastAdvancedQueueIdRef.current = '';
      progressRef.current = {
        queueId: '',
        time: -1,
        seenAt: 0,
      };
      handledEndedRef.current = {
        queueId: '',
        provider: '',
        at: 0,
      };
      setPlayerInfo({
        currentTime: 0,
        duration: 0,
        playerState: YT_PLAYER_STATE_UNSTARTED,
        loadedFraction: 0,
        videoDataId: '',
        lastMessageAt: 0,
      });
      lastAppliedSpotifyBootstrapRef.current = {
        mediaKey: '',
        playing: false,
        updatedAt: 0,
        at: 0,
      };
      spotifyBootstrapGuardRef.current = {
        mediaKey: '',
        until: 0,
      };
      return;
    }

    const activeChanged = lastSyncedActiveMediaKeyRef.current !== nextActiveKey;

    if (safeProvider(nextActive.provider) === 'spotify') {
      const nextUri = String(nextActive.mediaUri || '').trim();

      setLocalProvider('spotify');
      setLocalMediaKey(nextActiveKey);
      setLocalEmbedUrl(buildSpotifyEmbedUrl(nextActive));
      ensureSpotifyVisibleHost();

      if (activeChanged) {
        setPlayerLoaded(false);
        pendingPlaybackRef.current = null;
        lastAppliedSpotifyBootstrapRef.current = {
          mediaKey: '',
          playing: false,
          updatedAt: 0,
          at: 0,
        };
        spotifyBootstrapGuardRef.current = {
          mediaKey: nextActiveKey,
          until: Date.now() + 8000,
        };

        setTimeout(() => {
          loadSpotifyItemIntoController(nextUri, 0, false).catch(() => {});
        }, 0);

        setTimeout(() => {
          const currentPlayback = sharedPlaybackRef.current;
          if (currentPlayback && safeProvider(currentPlayback.provider) === 'spotify') {
            syncSpotifyBeforeLocalPlaybackStarts(currentPlayback, { force: true }).catch(() => {});
          }
        }, 900);
      }
    } else {
      if (activeChanged) {
        setSpotifyPlayerActivated(false);
        setSpotifyEmbedPrimed(false);
        setLocalProvider(safeProvider(nextActive.provider));
        setLocalMediaKey(nextActiveKey);
        setLocalEmbedUrl(buildEmbedUrlForItem(nextActive, false, 0));
        setPlayerLoaded(false);
      }
      spotifyBootstrapGuardRef.current = {
        mediaKey: '',
        until: 0,
      };
    }

    lastSyncedActiveMediaKeyRef.current = nextActiveKey;

    if (lastAdvancedQueueIdRef.current && lastAdvancedQueueIdRef.current !== nextActiveQueueId) {
      lastAdvancedQueueIdRef.current = '';
    }

    handledEndedRef.current = {
      queueId: nextActiveQueueId,
      provider: '',
      at: 0,
    };

    if (progressRef.current.queueId !== nextActiveQueueId) {
      progressRef.current = {
        queueId: nextActiveQueueId,
        time: -1,
        seenAt: 0,
      };
    }
  }, [activeItem, ensureSpotifyVisibleHost, identity, loadSpotifyItemIntoController, registerSpotifyPeerProgress, syncSpotifyBeforeLocalPlaybackStarts]);

  useEffect(() => {
    if (localProvider === 'spotify') {
      ensureSpotifyVisibleHost();
    }
  }, [ensureSpotifyVisibleHost, localProvider, localMediaKey]);

  useEffect(() => {
    const pending = pendingSpotifyAutoStartRef.current;
    const currentActive = activeItemRef.current;
    const currentKey = itemMediaKey(currentActive);

    if (!pending || !currentActive || safeProvider(currentActive.provider) !== 'spotify') return;
    if (!currentKey || currentKey !== pending.mediaKey) return;

    let cancelled = false;

    const run = async () => {
      try {
        await directPlaySpotifyLocally(safeNumber(pending.currentTime, 0));
        if (cancelled) return;

        if (pending.publishShared) {
          const nextPlayback = buildPlaybackFromItem(currentActive, true, safeNumber(pending.currentTime, 0));
          setSharedPlayback(nextPlayback);
          sharedPlaybackRef.current = nextPlayback;
          await publishSpotifySharedPlayback(nextPlayback);
        }

        if (pending.publishRemote) {
          await publishRemoteSpotifyPlayback({
            serverId,
            mediaId: currentActive.mediaId,
            mediaType: currentActive.mediaType,
            mediaUri: currentActive.mediaUri,
            currentTime: safeNumber(pending.currentTime, 0),
            action: 'play',
          }, room);
        }

        if (!cancelled) {
          pendingSpotifyAutoStartRef.current = null;
        }
      } catch {}
    };

    const timer = setTimeout(() => {
      run();
    }, 20);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [activeItem, directPlaySpotifyLocally, publishSpotifySharedPlayback, room, serverId]);

  useEffect(() => {
    if (!isShowingActiveLocally) return undefined;
    if (localProvider !== 'youtube') return undefined;

    if (infoRequestTimerRef.current) {
      clearInterval(infoRequestTimerRef.current);
      infoRequestTimerRef.current = null;
    }

    const timer = setInterval(() => {
      bootstrapPlayerMessaging();
      sendPlayerCommand('getCurrentTime');
      sendPlayerCommand('getDuration');
      sendPlayerCommand('getPlayerState');
      sendPlayerCommand('getVideoData');
      sendPlayerCommand('getVideoLoadedFraction');
    }, INFO_POLL_MS);

    infoRequestTimerRef.current = timer;

    return () => {
      clearInterval(timer);
      if (infoRequestTimerRef.current === timer) {
        infoRequestTimerRef.current = null;
      }
    };
  }, [bootstrapPlayerMessaging, isShowingActiveLocally, localProvider, sendPlayerCommand]);

  useEffect(() => {
    const currentActive = activeItemRef.current;
    const queueId = String((currentActive && currentActive.id) || '').trim();
    const currentMediaKey = itemMediaKey(currentActive);

    if (!queueId || !currentMediaKey) return;
    if (!isShowingActiveLocally) return;
    if (!shareReady) return;
    if (advanceInFlightRef.current) return;
    if (lastAdvancedQueueIdRef.current === queueId) return;

    if (safeProvider(currentActive.provider) === 'spotify') {
      const duration = safeNumber(playerInfo.duration, 0);
      const currentTime = safeNumber(playerInfo.currentTime, 0);
      const guard = spotifyBootstrapGuardRef.current;
      const remaining = computeRemainingSeconds(currentTime, duration);

      if (
        guard.mediaKey === currentMediaKey &&
        safeNumber(guard.until, 0) > Date.now() &&
        currentTime >= 3
      ) {
        spotifyBootstrapGuardRef.current = {
          mediaKey: '',
          until: 0,
        };
      }

      if (
        duration > 0 &&
        remaining <= END_THRESHOLD_SECONDS &&
        !(guard.mediaKey === currentMediaKey && safeNumber(guard.until, 0) > Date.now())
      ) {
        const handled = handledEndedRef.current;
        if (!(handled.queueId === queueId && handled.provider === 'spotify' && Date.now() - safeNumber(handled.at, 0) < 4000)) {
          handledEndedRef.current = {
            queueId,
            provider: 'spotify',
            at: Date.now(),
          };
          advanceQueueFromActive(queueId, currentMediaKey).catch(() => {});
        }
      }
      return;
    }

    if (safeProvider(currentActive.provider) !== 'youtube') return;

    const duration = safeNumber(playerInfo.duration, 0);
    const currentTime = safeNumber(playerInfo.currentTime, 0);
    const playerState = safeNumber(playerInfo.playerState, YT_PLAYER_STATE_UNSTARTED);

    if (duration <= 0) return;

    const remaining = computeRemainingSeconds(currentTime, duration);
    const definitelyEnded = playerState === YT_PLAYER_STATE_ENDED || remaining <= END_THRESHOLD_SECONDS;

    const p = progressRef.current;

    if (p.queueId !== queueId) {
      progressRef.current = {
        queueId,
        time: currentTime,
        seenAt: Date.now(),
      };
      return;
    }

    if (currentTime > p.time + 0.05) {
      progressRef.current = {
        queueId,
        time: currentTime,
        seenAt: Date.now(),
      };
      return;
    }

    const stalledLongEnough = Date.now() - p.seenAt >= END_STALL_WINDOW_MS;

    if (definitelyEnded && stalledLongEnough) {
      const handled = handledEndedRef.current;
      if (!(handled.queueId === queueId && handled.provider === 'youtube' && Date.now() - safeNumber(handled.at, 0) < 4000)) {
        handledEndedRef.current = {
          queueId,
          provider: 'youtube',
          at: Date.now(),
        };
        advanceQueueFromActive(queueId, currentMediaKey).catch(() => {});
      }
      return;
    }

    progressRef.current = {
      queueId,
      time: currentTime,
      seenAt: p.seenAt || Date.now(),
    };
  }, [advanceQueueFromActive, isShowingActiveLocally, playerInfo, shareReady]);

  useEffect(() => {
    const handleMessage = (event) => {
      // Only the embedded YouTube player may report player state.
      const origin = String((event && event.origin) || '');
      if (!/^https:\/\/(www\.)?youtube(-nocookie)?\.com$/.test(origin)) return;
      let data = event && event.data;

      if (typeof data === 'string') {
        try {
          data = JSON.parse(data);
        } catch {
          return;
        }
      }

      if (!data || typeof data !== 'object') return;
      if (data.event === 'onError') {
        if (event.source && iframeRef.current && event.source !== iframeRef.current.contentWindow) return;
        setPlayerError(describeYouTubeError(data.info));
        return;
      }
      if (data.event === 'onReady') {
        setPlayerLoaded(true);
        setPlayerError('');
        bootstrapPlayerMessaging();

        if (pendingPlaybackRef.current) {
          const pending = pendingPlaybackRef.current;
          setTimeout(() => {
            applyPlaybackToPlayer(pending);
          }, 100);
        }

        return;
      }

      if (data.event !== 'infoDelivery') return;
      if (!data.info || typeof data.info !== 'object') return;

      // infoDelivery messages are deltas: a field that did not change is left out. Treating a missing
      // field as 0 / unstarted made every viewer look out of sync, so the drift check force-seeked all
      // players every 3 s (stutter) and native seeks were reverted.
      const info = { ...(ytInfoRef.current || {}), ...data.info };
      ytInfoRef.current = info;
      const currentTime = safeNumber(info.currentTime, 0);
      const duration = safeNumber(info.duration, 0);
      const playerState = safeNumber(info.playerState, YT_PLAYER_STATE_UNSTARTED);
      const loadedFraction = safeNumber(info.videoLoadedFraction, 0);
      const videoDataId = String((info.videoData && (info.videoData.video_id || info.videoData.videoId)) || '').trim();

      lastKnownPlayerStateRef.current = playerState;

      setPlayerLoaded(true);
      setPlayerError('');
      setPlayerInfo({
        currentTime,
        duration,
        playerState,
        loadedFraction,
        videoDataId,
        lastMessageAt: Date.now(),
      });

      if (playerState === YT_PLAYER_STATE_PLAYING && pendingPlaybackRef.current) {
        pendingPlaybackRef.current = null;
      }
      if (playerState === YT_PLAYER_STATE_PLAYING) playAttemptsRef.current = 0;

      // Pauses, plays and seeks made with the embedded player's own controls.
      const sample = {
        time: currentTime,
        at: Date.now(),
        playing: playerState === YT_PLAYER_STATE_PLAYING,
        buffering: playerState === YT_PLAYER_STATE_BUFFERING,
      };
      // Compare against the last settled sample; buffering samples only mark that a stall happened.
      const prevSample = lastPlayerSampleRef.current;
      const stalled = stalledSinceSampleRef.current;
      if (sample.buffering) {
        stalledSinceSampleRef.current = true;
      } else {
        lastPlayerSampleRef.current = sample;
        stalledSinceSampleRef.current = false;
      }
      const shared = sharedPlaybackRef.current;
      if (
        !sample.buffering &&
        prevSample &&
        shared &&
        safeProvider(shared.provider) === 'youtube' &&
        playbackMediaKey(shared) === itemMediaKey(activeItemRef.current) &&
        playerState !== YT_PLAYER_STATE_ENDED
      ) {
        const intent = detectLocalIntent({ prev: prevSample, cur: sample, playback: shared, ignoreUntil: ignoreLocalIntentUntilRef.current, stalled });
        if (intent && typeof localIntentHandlerRef.current === 'function') localIntentHandlerRef.current(intent);
      }

      if (playerState === YT_PLAYER_STATE_ENDED) {
        const currentActive = activeItemRef.current;
        const queueId = String((currentActive && currentActive.id) || '').trim();
        const currentMediaKey = itemMediaKey(currentActive);
        if (queueId && currentMediaKey && !advanceInFlightRef.current && lastAdvancedQueueIdRef.current !== queueId) {
          const handled = handledEndedRef.current;
          if (!(handled.queueId === queueId && handled.provider === 'youtube' && Date.now() - safeNumber(handled.at, 0) < 4000)) {
            handledEndedRef.current = {
              queueId,
              provider: 'youtube',
              at: Date.now(),
            };
            advanceQueueFromActive(queueId, currentMediaKey).catch(() => {});
          }
        }
      }
    };

    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [advanceQueueFromActive, applyPlaybackToPlayer, bootstrapPlayerMessaging]);

  useEffect(() => {
    if (!playerLoaded) return;
    if (!pendingPlaybackRef.current) return;
    if (!isShowingActiveLocally) return;
    if (localProvider !== 'youtube') return;

    const currentActive = activeItemRef.current;
    if (!currentActive) return;

    const pending = pendingPlaybackRef.current;
    const activeKey = itemMediaKey(currentActive);
    const pendingKey = playbackMediaKey(pending);
    if (!activeKey || !pendingKey || activeKey !== pendingKey) return;

    applyPlaybackToPlayer(pending);
  }, [applyPlaybackToPlayer, isShowingActiveLocally, localProvider, playerLoaded]);

  // When YouTube refuses an embed outright, the player never talks to us (no onReady / onError), so a
  // silent player after a while is reported instead of leaving a dead frame.
  useEffect(() => {
    if (localProvider !== 'youtube' || !localEmbedUrl) return undefined;
    const startedAt = Date.now();
    const t = setTimeout(() => {
      const info = playerInfoRef.current || {};
      if (safeNumber(info.lastMessageAt, 0) < startedAt) {
        setPlayerError('The video did not load. It may be unavailable in your region, private, or blocked from playing on other sites. Try skipping it or opening it on YouTube.');
      }
    }, 12000);
    return () => clearTimeout(t);
  }, [localEmbedUrl, localProvider]);

  // Periodic drift check: corrects only meaningful drift (slow buffering, sleep, late join).
  useEffect(() => {
    if (!playerLoaded) return undefined;
    if (!isShowingActiveLocally) return undefined;
    if (localProvider !== 'youtube' && localProvider !== 'misc') return undefined;

    let deferredTicks = 0;
    const timer = setInterval(() => {
      const p = sharedPlaybackRef.current;
      if (!p || safeProvider(p.provider) === 'spotify') return;
      if (playbackMediaKey(p) !== itemMediaKey(activeItemRef.current)) return;
      // The player is buffering and we did not cause it: most likely the user just seeked with the
      // player's own controls. Correcting now would snap them back before the seek can be shared.
      if (stalledSinceSampleRef.current && Date.now() > ignoreLocalIntentUntilRef.current && deferredTicks < 2) {
        deferredTicks += 1;
        return;
      }
      deferredTicks = 0;
      applyPlaybackToPlayer(p);
    }, 3000);

    return () => clearInterval(timer);
  }, [applyPlaybackToPlayer, isShowingActiveLocally, localProvider, playerLoaded]);

  useEffect(() => {
    return () => {
      if (infoRequestTimerRef.current) {
        clearInterval(infoRequestTimerRef.current);
      }
      if (bootstrapTimerRef.current) {
        clearTimeout(bootstrapTimerRef.current);
      }
      destroySpotifyController();
    };
  }, [destroySpotifyController]);

  const addToQueue = useCallback(async () => {
    if (!canLocalControlWatch(room)) {
      setPlayerError('Only the host can change the queue right now.');
      return;
    }
    if (!normalizedDraft || !normalizedDraft.ok || !shareReady) return;

    const nextEntry = queueEntryFromDraft(normalizedDraft, userName, identity);
    if (!nextEntry) {
      setPlayerError('Could not add that item.');
      return;
    }

    const nextQueue = [...sharedQueue, nextEntry];
    const nextActiveQueueId = activeQueueId || nextEntry.id;
    const nextPlayback = !activeQueueId ? buildPlaybackFromItem(nextEntry, false, 0) : undefined;

    setSharedQueue(nextQueue);
    if (!activeQueueId) {
      setActiveQueueId(nextEntry.id);
      setLocalProvider(safeProvider(nextEntry.provider));
      setLocalMediaKey(itemMediaKey(nextEntry));
      setLocalEmbedUrl(buildEmbedUrlForItem(nextEntry, false, 0));
      setPlayerLoaded(false);
      setPlayerError('');
      setSpotifyPlayerActivated(false);
      progressRef.current = {
        queueId: nextEntry.id,
        time: -1,
        seenAt: 0,
      };
    }

    setInput('');
    setProvider('youtube');

    await pushQueue(nextQueue, nextActiveQueueId, nextPlayback);

    if (nextPlayback && nextPlayback.mediaId) {
      if (safeProvider(nextEntry.provider) !== 'spotify') {
        await publishWatchQueuePlayback({ serverId, ...nextPlayback }, room);
      } else {
        await publishSpotifySharedPlayback(nextPlayback);
      }
    }
  }, [activeQueueId, identity, normalizedDraft, publishSpotifySharedPlayback, pushQueue, room, serverId, shareReady, sharedQueue, userName]);

  const makeActive = useCallback(async (queueId) => {
    if (!canLocalControlWatch(room)) {
      setPlayerError('Only the host can change what is playing right now.');
      return;
    }
    const targetId = String(queueId || '').trim();
    const target = sharedQueue.find((item) => item.id === targetId);
    if (!target || !shareReady) return;

    const nextPlayback = buildPlaybackFromItem(target, false, 0);
    const clearedSkipVote = {
      queueId: '',
      identities: [],
      updatedAt: Date.now(),
    };

    setActiveQueueId(targetId);
    setSharedPlayback(nextPlayback);
    setSkipVote(clearedSkipVote);
    sharedPlaybackRef.current = nextPlayback;
    skipVoteRef.current = clearedSkipVote;
    pendingPlaybackRef.current = safeProvider(target.provider) !== 'spotify' ? nextPlayback : null;
    pendingSpotifyAutoStartRef.current = null;
    setLocalProvider(safeProvider(target.provider));
    setLocalMediaKey(itemMediaKey(target));
    setLocalEmbedUrl(buildEmbedUrlForItem(target, false, 0));
    setPlayerLoaded(false);
    setPlayerError('');
    setSpotifyPlayerActivated(false);
    progressRef.current = {
      queueId: target.id,
      time: -1,
      seenAt: 0,
    };

    await pushQueue(sharedQueue, targetId, nextPlayback, clearedSkipVote);

    if (safeProvider(target.provider) !== 'spotify') {
      await publishWatchQueuePlayback({ serverId, ...nextPlayback }, room);
    } else {
      await publishSpotifySharedPlayback(nextPlayback);
    }
  }, [publishSpotifySharedPlayback, pushQueue, room, serverId, shareReady, sharedQueue]);

  const removeFromQueue = useCallback(async (queueId) => {
    if (!canLocalControlWatch(room)) {
      setPlayerError('Only the host can change the queue right now.');
      return;
    }
    const targetId = String(queueId || '').trim();
    const removedItem = sharedQueue.find((item) => item.id === targetId) || null;
    const nextQueue = sharedQueue.filter((item) => item.id !== targetId);

    let nextActiveQueueId = activeQueueId;
    if (nextActiveQueueId === targetId) {
      nextActiveQueueId = nextQueue[0] ? nextQueue[0].id : '';
    }

    let nextPlayback;
    let nextSkipVote;

    if (!nextQueue.length) {
      nextPlayback = removedItem ? buildPlaybackFromItem(removedItem, false, 0) : sharedPlaybackRef.current;
      nextSkipVote = {
        queueId: '',
        identities: [],
        updatedAt: Date.now(),
      };
      setLocalProvider('');
      setLocalMediaKey('');
      setLocalEmbedUrl('');
      setPlayerLoaded(false);
      setPlayerError('');
      setSpotifyPlayerActivated(false);
      setSpotifyEmbedPrimed(false);
      pendingPlaybackRef.current = null;
      pendingSpotifyAutoStartRef.current = null;
      progressRef.current = {
        queueId: '',
        time: -1,
        seenAt: 0,
      };
      setSkipVote(nextSkipVote);
      skipVoteRef.current = nextSkipVote;
    } else if (targetId === activeQueueId) {
      const nextItem = nextQueue[0] || null;
      if (nextItem) {
        nextPlayback = buildPlaybackFromItem(nextItem, false, 0);
        nextSkipVote = {
          queueId: '',
          identities: [],
          updatedAt: Date.now(),
        };
        setSharedPlayback(nextPlayback);
        setSkipVote(nextSkipVote);
        sharedPlaybackRef.current = nextPlayback;
        skipVoteRef.current = nextSkipVote;
        pendingPlaybackRef.current = safeProvider(nextItem.provider) !== 'spotify' ? nextPlayback : null;
        pendingSpotifyAutoStartRef.current = null;
        setLocalProvider(safeProvider(nextItem.provider));
        setLocalMediaKey(itemMediaKey(nextItem));
        setLocalEmbedUrl(buildEmbedUrlForItem(nextItem, false, 0));
        setPlayerLoaded(false);
        setPlayerError('');
        setSpotifyPlayerActivated(false);
        progressRef.current = {
          queueId: nextItem.id,
          time: -1,
          seenAt: 0,
        };
      }
    } else {
      const currentSkipVote = skipVoteRef.current;
      if (currentSkipVote && currentSkipVote.queueId === targetId) {
        nextSkipVote = {
          queueId: '',
          identities: [],
          updatedAt: Date.now(),
        };
        setSkipVote(nextSkipVote);
        skipVoteRef.current = nextSkipVote;
      }
    }

    await pushQueue(nextQueue, nextActiveQueueId, nextPlayback, nextSkipVote);

    if (nextPlayback && nextPlayback.mediaId) {
      const targetProvider = nextQueue[0] ? safeProvider(nextQueue[0].provider) : safeProvider(removedItem?.provider);
      if (targetProvider !== 'spotify') {
        await publishWatchQueuePlayback({ serverId, ...nextPlayback }, room);
      } else {
        await publishSpotifySharedPlayback(nextPlayback);
      }
    }
  }, [activeQueueId, publishSpotifySharedPlayback, pushQueue, room, serverId, sharedQueue]);

  const clearQueue = useCallback(async () => {
    if (!canLocalControlWatch(room)) {
      setPlayerError('Only the host can change the queue right now.');
      return;
    }
    const currentActive = activeItemRef.current;
    const stoppedPlayback = currentActive ? buildPlaybackFromItem(currentActive, false, 0) : sharedPlaybackRef.current || undefined;

    const clearedSkipVote = {
      queueId: '',
      identities: [],
      updatedAt: Date.now(),
    };

    setLocalProvider('');
    setLocalMediaKey('');
    setLocalEmbedUrl('');
    setPlayerLoaded(false);
    setPlayerError('');
    setSpotifyPlayerActivated(false);
    setSpotifyEmbedPrimed(false);
    setSkipVote(clearedSkipVote);
    skipVoteRef.current = clearedSkipVote;
    pendingPlaybackRef.current = null;
    pendingSpotifyAutoStartRef.current = null;
    progressRef.current = {
      queueId: '',
      time: -1,
      seenAt: 0,
    };

    try {
      const controller = spotifyPlayerRef.current;
      if (controller && typeof controller.pause === 'function') {
        controller.pause();
      }
    } catch {}

    await pushQueue([], '', stoppedPlayback, clearedSkipVote);

    if (stoppedPlayback && stoppedPlayback.mediaId) {
      if (safeProvider(stoppedPlayback.provider) !== 'spotify') {
        await publishWatchQueuePlayback({ serverId, ...stoppedPlayback }, room);
      } else {
        await publishSpotifySharedPlayback(stoppedPlayback);
      }
    }
  }, [publishSpotifySharedPlayback, pushQueue, room, serverId]);

  const playForEveryone = useCallback(async () => {
    if (!canLocalControlWatch(room)) {
      setPlayerError('Only the host can control playback right now.');
      return;
    }
    const currentActive = activeItemRef.current;
    const currentMediaKey = itemMediaKey(currentActive);
    if (!currentActive || !currentMediaKey || !shareReady) return;

    if (safeProvider(currentActive.provider) === 'spotify') {
      const baseTime = safeNumber(playerInfo.currentTime, 0);
      const nextPlayback = buildPlaybackFromItem(currentActive, true, baseTime);

      setSharedPlayback(nextPlayback);
      sharedPlaybackRef.current = nextPlayback;
      pendingPlaybackRef.current = null;

      lastLocalSpotifyRemoteCommandRef.current = {
        mediaKey: currentMediaKey,
        action: 'play',
        at: Date.now(),
        updatedAt: Date.now(),
      };

      await directPlaySpotifyLocally(baseTime);
      await publishSpotifySharedPlayback(nextPlayback);
      await publishRemoteSpotifyPlayback({
        serverId,
        mediaId: currentActive.mediaId,
        mediaType: currentActive.mediaType,
        mediaUri: currentActive.mediaUri,
        currentTime: baseTime,
        action: 'play',
      }, room);
      return;
    }

    const currentPlayback = sharedPlaybackRef.current;
    const baseTime = currentPlayback && playbackMediaKey(currentPlayback) === currentMediaKey
      ? computePlaybackTime(currentPlayback)
      : 0;

    const nextPlayback = buildPlaybackFromItem(currentActive, true, baseTime);

    setSharedPlayback(nextPlayback);
    sharedPlaybackRef.current = nextPlayback;
    pendingPlaybackRef.current = nextPlayback;
    applyPlaybackToPlayer(nextPlayback);

    await publishWatchQueuePlayback({ serverId, ...nextPlayback }, room);
  }, [applyPlaybackToPlayer, directPlaySpotifyLocally, playerInfo.currentTime, publishSpotifySharedPlayback, room, serverId, shareReady]);

  const pauseForEveryone = useCallback(async () => {
    if (!canLocalControlWatch(room)) {
      setPlayerError('Only the host can control playback right now.');
      return;
    }
    const currentActive = activeItemRef.current;
    const currentMediaKey = itemMediaKey(currentActive);
    if (!currentActive || !currentMediaKey || !shareReady) return;

    if (safeProvider(currentActive.provider) === 'spotify') {
      const pausedTime = safeNumber(playerInfo.currentTime, 0);
      const nextPlayback = buildPlaybackFromItem(currentActive, false, pausedTime);

      setSharedPlayback(nextPlayback);
      sharedPlaybackRef.current = nextPlayback;
      pendingPlaybackRef.current = null;

      lastLocalSpotifyRemoteCommandRef.current = {
        mediaKey: currentMediaKey,
        action: 'pause',
        at: Date.now(),
        updatedAt: Date.now(),
      };

      await directPauseSpotifyLocally(pausedTime);
      await publishSpotifySharedPlayback(nextPlayback);
      await publishRemoteSpotifyPlayback({
        serverId,
        mediaId: currentActive.mediaId,
        mediaType: currentActive.mediaType,
        mediaUri: currentActive.mediaUri,
        currentTime: pausedTime,
        action: 'pause',
      }, room);
      return;
    }

    const currentPlayback = sharedPlaybackRef.current;
    const pausedTime = currentPlayback && playbackMediaKey(currentPlayback) === currentMediaKey
      ? computePlaybackTime(currentPlayback)
      : safeNumber(playerInfo.currentTime, 0);

    const nextPlayback = buildPlaybackFromItem(currentActive, false, pausedTime);

    setSharedPlayback(nextPlayback);
    sharedPlaybackRef.current = nextPlayback;
    pendingPlaybackRef.current = nextPlayback;
    applyPlaybackToPlayer(nextPlayback);

    await publishWatchQueuePlayback({ serverId, ...nextPlayback }, room);
  }, [applyPlaybackToPlayer, directPauseSpotifyLocally, playerInfo.currentTime, publishSpotifySharedPlayback, room, serverId, shareReady]);

  // Shared seek / play / pause at an explicit position (YouTube and direct media).
  const seekForEveryone = useCallback(async (time, playingOverride) => {
    const currentActive = activeItemRef.current;
    if (!currentActive || !shareReady) return;
    if (safeProvider(currentActive.provider) === 'spotify') return;
    if (!canLocalControlWatch(room)) return;

    const current = sharedPlaybackRef.current;
    const playing = typeof playingOverride === 'boolean' ? playingOverride : !!(current && current.playing);
    const nextPlayback = buildPlaybackFromItem(currentActive, playing, Math.max(0, safeNumber(time, 0)));

    setSharedPlayback(nextPlayback);
    sharedPlaybackRef.current = nextPlayback;
    pendingPlaybackRef.current = null;
    ignoreLocalIntentUntilRef.current = Date.now() + 1500;

    await publishWatchQueuePlayback({ serverId, ...nextPlayback }, room);
  }, [room, serverId, shareReady]);

  useEffect(() => {
    localIntentHandlerRef.current = (intent) => {
      if (!intent) return;
      if (!canLocalControlWatch(room)) {
        // Viewers who may not control playback are put back on the shared timeline.
        const p = sharedPlaybackRef.current;
        if (p) applyPlaybackToPlayer(p);
        return;
      }
      const playing = intent.kind === 'play' ? true : intent.kind === 'pause' ? false : !!intent.playing;
      seekForEveryone(intent.time, playing).catch(() => {});
    };
  }, [applyPlaybackToPlayer, room, seekForEveryone]);

  // Host hand-over when the host leaves the voice room.
  useEffect(() => {
    if (!room || typeof room.on !== 'function') return undefined;
    const onChange = () => {
      reconcileWatchHost(room);
    };
    room.on('participantDisconnected', onChange);
    room.on('participantConnected', onChange);
    onChange();
    return () => {
      try {
        room.off('participantDisconnected', onChange);
        room.off('participantConnected', onChange);
      } catch {}
    };
  }, [room]);

  const unmuteLocalPlayer = useCallback(() => {
    sendPlayerCommand('unMute');
    setAutoplayMuted(false);
  }, [sendPlayerCommand]);

  const setControlMode = useCallback(async (mode) => {
    if (!shareReady) return false;
    if (!canLocalControlWatch(room)) {
      setPlayerError('Only the host can change who controls playback.');
      return false;
    }
    return publishWatchControlMode(mode, room);
  }, [room, shareReady]);

  // Watch Together rights from the server-signed participant attributes (re-read when they change),
  // combined with the session's host-only mode. Receivers enforce the same rules (watchPermissions.js).
  const [, setPermsTick] = useState(0);
  useEffect(() => {
    const bump = () => setPermsTick((n) => n + 1);
    window.addEventListener('vox-permissions-changed', bump);
    return () => window.removeEventListener('vox-permissions-changed', bump);
  }, []);
  const watchPerms = localWatchPerms(room);
  const sessionManager = !!watchPerms && watchPerms.has(WATCH_PERMS.manageSessions);
  const byMode = canControl({ mode: controlMode, hostIdentity, identity }) || sessionManager;
  const canControlPlayback = canLocalControlWatch(room);
  const canAddToQueue = byMode && hasPerm(watchPerms, WATCH_PERMS.add) && (sharedQueue.length > 0 || hasPerm(watchPerms, WATCH_PERMS.start));
  const canManageQueue = byMode && hasPerm(watchPerms, WATCH_PERMS.manageQueue);
  const canRemoveOwnItems = byMode && hasPerm(watchPerms, WATCH_PERMS.removeOwn);
  const canChooseActive = canManageQueue || canControlPlayback;
  const canSwitchMode = sessionManager || (identity && String(hostIdentity || '') === identity);
  const hostName = useMemo(() => {
    if (!hostIdentity) return '';
    if (hostIdentity === identity) return 'You';
    try {
      const p = room && room.remoteParticipants ? room.remoteParticipants.get(hostIdentity) : null;
      return String((p && (p.name || p.identity)) || hostIdentity);
    } catch {
      return hostIdentity;
    }
  }, [hostIdentity, identity, room]);

  const toggleSkipVote = useCallback(async () => {
    const currentActive = activeItemRef.current;
    const queueId = String((currentActive && currentActive.id) || '').trim();
    if (!queueId || !shareReady || !identity) return;

    const voted = !hasVotedToSkip;

    await publishWatchQueueSkipVote(
      {
        serverId,
        queueId,
        voterIdentity: identity,
        voted,
      },
      room
    );
  }, [hasVotedToSkip, identity, room, serverId, shareReady]);

  const connectSpotify = useCallback(async () => {
    setSpotifyAuthBusy(true);
    setPlayerError('');
    try {
      await startSpotifyLogin();
    } catch (error) {
      setSpotifyAuthBusy(false);
      setPlayerError(error?.message || 'Could not start Spotify login.');
    }
  }, []);

  const disconnectSpotify = useCallback(() => {
    clearSpotifySession();
    destroySpotifyController();
    setSpotifySession(null);
    setSpotifyAuthBusy(false);
    setSpotifyPlayerActivated(false);
    setSpotifyEmbedPrimed(false);
    setSyncStatus('Spotify disconnected');
  }, [destroySpotifyController]);

  const onIframeLoad = useCallback(() => {
    if (localProvider !== 'youtube') return;

    setPlayerError('');
    setPlayerLoaded(false);

    if (bootstrapTimerRef.current) {
      clearTimeout(bootstrapTimerRef.current);
    }

    bootstrapPlayerMessaging();

    bootstrapTimerRef.current = setTimeout(() => {
      bootstrapPlayerMessaging();
      sendPlayerCommand('getDuration');
      sendPlayerCommand('getVideoData');
      sendPlayerCommand('getPlayerState');
    }, 500);

    if (pendingPlaybackRef.current) {
      const pending = pendingPlaybackRef.current;
      setTimeout(() => {
        applyPlaybackToPlayer(pending);
      }, 700);
    }
  }, [applyPlaybackToPlayer, bootstrapPlayerMessaging, localProvider, sendPlayerCommand]);

  const onDirectMediaLoaded = useCallback(() => {
    const mediaEl = mediaRef.current;
    const currentActive = activeItemRef.current;
    if (!mediaEl || !currentActive || safeProvider(currentActive.provider) !== 'misc') return;

    setPlayerLoaded(true);
    setPlayerError('');
    setPlayerInfo({
      currentTime: Number(mediaEl.currentTime) || 0,
      duration: Number.isFinite(Number(mediaEl.duration)) ? Number(mediaEl.duration) : 0,
      playerState: mediaEl.paused ? YT_PLAYER_STATE_UNSTARTED : YT_PLAYER_STATE_PLAYING,
      loadedFraction: 1,
      videoDataId: String(currentActive.mediaId || ''),
      lastMessageAt: Date.now(),
    });

    if (pendingPlaybackRef.current) {
      const pending = pendingPlaybackRef.current;
      pendingPlaybackRef.current = null;
      setTimeout(() => {
        applyPlaybackToPlayer(pending);
      }, 0);
    }
  }, [applyPlaybackToPlayer]);

  const onDirectMediaTimeUpdate = useCallback(() => {
    const mediaEl = mediaRef.current;
    const currentActive = activeItemRef.current;
    if (!mediaEl || !currentActive || safeProvider(currentActive.provider) !== 'misc') return;

    setPlayerInfo({
      currentTime: Number(mediaEl.currentTime) || 0,
      duration: Number.isFinite(Number(mediaEl.duration)) ? Number(mediaEl.duration) : 0,
      playerState: mediaEl.paused ? YT_PLAYER_STATE_UNSTARTED : YT_PLAYER_STATE_PLAYING,
      loadedFraction: 1,
      videoDataId: String(currentActive.mediaId || ''),
      lastMessageAt: Date.now(),
    });
  }, []);

  const onDirectMediaEnded = useCallback(() => {
    const currentActive = activeItemRef.current;
    const currentKey = itemMediaKey(currentActive);
    if (!currentActive || safeProvider(currentActive.provider) !== 'misc' || !currentKey) return;
    advanceQueueFromActive(String(currentActive.id || ''), currentKey).catch(() => {});
  }, [advanceQueueFromActive]);

  useEffect(() => {
    const currentActive = activeItemRef.current;
    const currentKey = itemMediaKey(currentActive);

    if (!shareReady) return;
    if (!currentActive || !currentKey) return;
    if (activeSkipVotes.length < majorityNeeded) return;

    advanceQueueFromActive(String(currentActive.id || ''), currentKey).catch(() => {});
  }, [activeSkipVotes.length, advanceQueueFromActive, majorityNeeded, shareReady]);

  return {
    provider,
    setProvider,
    providers: PROVIDERS,
    input,
    setInput,
    normalizedDraft,
    sharedQueue,
    activeQueueId,
    skipVote,
    syncStatus,
    playerError,
    setPlayerError,
    localProvider,
    localMediaKey,
    localEmbedUrl,
    playerLoaded,
    sharedPlayback,
    playerInfo,
    iframeRef,
    mediaRef,
    spotifyMountRef,
    room,
    identity,
    userName,
    shareReady,
    participantCount,
    majorityNeeded,
    activeItem,
    activeSkipVotes,
    hasVotedToSkip,
    isShowingActiveLocally,
    spotifySession,
    spotifyAuthBusy,
    spotifyPlayerActivated,
    spotifyPlayerActivationBusy,
    spotifyEmbedPrimed,
    addToQueue,
    makeActive,
    removeFromQueue,
    clearQueue,
    playForEveryone,
    pauseForEveryone,
    seekForEveryone,
    resyncToSharedPlayback,
    autoplayMuted,
    unmuteLocalPlayer,
    controlMode,
    setControlMode,
    hostIdentity,
    hostName,
    canControlPlayback,
    canAddToQueue,
    canManageQueue,
    canRemoveOwnItems,
    canChooseActive,
    canSwitchMode,
    identity,
    toggleSkipVote,
    onIframeLoad,
    onDirectMediaLoaded,
    onDirectMediaTimeUpdate,
    onDirectMediaEnded,
    connectSpotify,
    disconnectSpotify,
    activateSpotifyPlayer,
    markSpotifyEmbedPrimed,
    voiceSession,
  };
}
